import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthService } from 'src/app/services/auth.service';
import { Role } from 'src/app/entities/role';
import { RoleName } from 'src/app/entities/util';
import { JwtHelperService } from '@auth0/angular-jwt';
import { SharedService } from 'src/app/services/sharedService';
import { ServiceService } from 'src/app/services/service.service';
import { distinctUntilChanged, first, timeout } from 'rxjs/operators';
import { authErrorKeys } from 'src/app/util/auth-error.util';

/**
 * 'redirecting' | 'error' | 'disabled' are the original SSO states and behave
 * exactly as before. The rest are the local-authentication flow, reached only
 * when the server advertises `local: true`.
 */
type LoginStatus = 'redirecting' | 'error' | 'disabled'
  | 'password' | 'mfa' | 'recovery' | 'enrol';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    // The shared auth-shell stylesheet is listed FIRST so this page's own rules
    // still win on any class they both define -- the existing SSO states must
    // look exactly as they did.
    styleUrls: ['../auth/auth-shell.css', './login.component.css'],
    standalone: false
})
export class LoginComponent implements OnInit {
  // A stalled SSO/token call used to leave this at its initial 'redirecting'
  // value forever, with no error and no way out: window.location.href only
  // gets assigned on the success path, and failures were caught but ignored.
  // SSO_TIMEOUT_MS + the 'error' status guarantee the flow always resolves to
  // something the user can act on (a retry button) instead of hanging.
  private static readonly SSO_TIMEOUT_MS = 15000;

  title = 'app';
  public singleModel: String = '1';
  public version: String = '1.0.0';
  roleNames = RoleName
  public error: string;
  public username: string;
  public password: string;
  status: LoginStatus = 'redirecting';
  user;

  /** Local-authentication state. All unused when the deployment is SSO-only. */
  localEnabled = false;
  ssoEnabled = true;
  allowedMfaMethods: string[] = ['totp', 'email'];
  busy = false;
  errorKeys: string[] = [];
  challenge = '';
  mfaMethod: 'totp' | 'email';
  recoveryAvailable = false;
  /** Whether the deployment permits an emailed code as a fallback. */
  emailFallbackAvailable = false;
  /** Set once a code has actually been sent, so the screen stops promising one. */
  emailCodeSent = false;
  /** How many days a remembered device lasts, or 0 when the deployment says never. */
  trustedDeviceDays = 0;
  /** The checkbox. Defaults OFF: skipping a second factor is an opt-in. */
  rememberDevice = false;
  code = '';
  recoveryCode = '';

  private nonce: any;
  private urlParam: any;
  private retUrlParam: any;

  constructor(private route: ActivatedRoute,
    private router: Router, public auth: AuthService, public sharedService: SharedService, public jwtHelper: JwtHelperService,
    private serviceService: ServiceService) {
  }

  ngOnInit() {
    this.route.queryParams
      .pipe(
        distinctUntilChanged()
      )
      .subscribe(params => {
        this.nonce = params['nonce']
        this.urlParam = params['url']
        this.retUrlParam = params['returnUrl']
        this.resolveSession()
      });
  }

  retry() {
    this.status = 'redirecting';
    this.resolveSession();
  }

  /**
   * Decides which sign-in the page offers.
   *
   * A deployment with local authentication off behaves EXACTLY as before: the
   * page redirects straight to the SSO server without showing anything. A
   * deployment with it on shows the password form, and offers SSO as a second
   * option when both are available.
   *
   * A failed probe falls back to the SSO redirect on purpose. `/web/auth/providers`
   * does not exist on a fusion-server built before this feature, so this build
   * must keep working against an un-upgraded API rather than presenting a form
   * whose endpoints 404.
   */
  private resolveProviders() {
    this.auth.providers$()
      .pipe(timeout(LoginComponent.SSO_TIMEOUT_MS), first())
      .subscribe({
        next: (providers) => {
          this.localEnabled = !!providers.local;
          this.ssoEnabled = providers.sso !== false;
          if (providers.allowedMfaMethods && providers.allowedMfaMethods.length) {
            this.allowedMfaMethods = providers.allowedMfaMethods;
          }
          if (this.localEnabled) {
            this.status = 'password';
          } else {
            this.redirectToSso();
          }
        },
        error: () => this.redirectToSso()
      });
  }

  /** The original no-nonce branch, unchanged, extracted so both callers share it. */
  private redirectToSso() {
    this.status = 'redirecting';
    this.auth.getRedirectLink()
        .pipe(timeout(LoginComponent.SSO_TIMEOUT_MS), first())
        .subscribe(
          result => {
            var uP = "";
            if (this.urlParam !== undefined) {
              if (typeof this.urlParam === "string") {
                uP = "/" + this.safePathSegment(this.urlParam);
              } else {
                this.urlParam.forEach((elem, index) => {
                  uP += "/" + this.safePathSegment(elem)
                })
              }
            }
            window.location.href = result['redirectLink'] + (uP !== "" ? uP : "")
          },
          err => {
            this.handleError(err)
          }
        );
  }

  private resolveSession() {
    if (!this.nonce) {
      // Ask the server which providers exist rather than assuming SSO.
      this.resolveProviders();
    }
    else {
      if (localStorage.getItem('access_token') === 'undefined'
        || this.jwtHelper.isTokenExpired()) {
        this.auth.getTokenFromNonce(this.nonce)
          .pipe(timeout(LoginComponent.SSO_TIMEOUT_MS), first())
          .subscribe(
            result => {
              if (result['statusCode'] === 200) {
                var tokenLocal = result['usrToken']
                localStorage.setItem('access_token', tokenLocal);
                this.sharedService.updateAccessToken()
                this.auth.getCurrentUser().subscribe({
                  next: user => {
                    this.user = user
                    if (user['status'] !== 'DISABLED') {
                      this.auth.setUserEnabled(true);
                      window.location.href = this.safeReturnUrl(this.retUrlParam)
                    }
                    else {
                      this.auth.setUserEnabled(false);
                      this.auth.setLoginStatus(true)
                      this.status = 'disabled';
                    }
                  },
                  error: err => this.handleError(err)
                })
              } else {
                this.handleError({ status: result['statusCode'] })
              }
            },
            err => {
              this.handleError(err)
            }
          );
      } else {
        this.auth.getCurrentUser().subscribe({
          next: user => {
            this.user = user
            if (user['status'] === 'ENABLED') {
              this.auth.setUserEnabled(true);
              window.location.href = this.safeReturnUrl(this.retUrlParam)
            }
            else {
              this.auth.setUserEnabled(false);
              this.auth.setLoginStatus(true)
              this.status = 'disabled';
            }
          },
          error: err => this.handleError(err)
        })
      }
    }
  }

  /**
   * Step one of local sign-in.
   *
   * The previous body of this method posted to `auth/login` and stored
   * `result.token` directly. That endpoint is unmounted server-side and the
   * tokens it minted carried no audience/issuer, so the API rejected them --
   * the method could never have worked, and nothing in the template called it.
   *
   * A correct password now yields a challenge, never a session, so this
   * advances to the second factor rather than navigating anywhere.
   */
  login() {
    if (!this.username || !this.password || this.busy) return;
    this.busy = true;
    this.errorKeys = [];
    this.auth.localLogin(this.username, this.password).subscribe({
      next: (res) => {
        this.busy = false;
        this.challenge = res.challenge;
        // Drop the plaintext as soon as the exchange is done with it.
        this.password = '';
        /*
         * "Don't ask again on this device": the server recognised the device
         * cookie against THIS account and signed the user in on the password
         * alone. There is no challenge in that response and no second factor to
         * ask for -- which is the whole point of the feature.
         *
         * Without this branch the screen fell through to the code form holding
         * an undefined challenge, so a correct authenticator code was posted
         * without one and came back CHALLENGE_INVALID: "this sign-in has
         * expired", on a sign-in that had in fact just succeeded. Invisible
         * until localAuth.trustedDeviceDays was turned on, because a server
         * that never trusts a device never sends this stage.
         */
        if (res.stage === 'complete' && res.token) {
          this.completeLogin(res.token);
          return;
        }
        if (res.stage === 'mfa-enrolment') {
          this.allowedMfaMethods = res.allowedMfaMethods || this.allowedMfaMethods;
          this.status = 'enrol';
        } else {
          this.mfaMethod = res.mfaMethod;
          this.recoveryAvailable = !!res.recoveryAvailable;
          this.emailFallbackAvailable = !!res.emailFallbackAvailable;
          this.trustedDeviceDays = Number(res.trustedDeviceDays) || 0;
          this.rememberDevice = false;
          // An account enrolled for email has already been sent one by cmd:'login'.
          this.emailCodeSent = res.mfaMethod === 'email';
          this.code = '';
          this.status = 'mfa';
        }
      },
      error: (err) => {
        this.busy = false;
        this.password = '';
        this.errorKeys = authErrorKeys(err);
      }
    });
  }

  /**
   * "Email me a code instead", and its resend.
   *
   * `emailCodeSent` is set from the RESPONSE, never optimistically. Today's
   * diagnosis was slowed by exactly that distinction: a screen saying "we have
   * emailed you a code" is a claim about something that either happened or did
   * not, and if the send fails the user must be told, not reassured.
   */
  requestEmailCode() {
    if (this.busy) return;
    this.busy = true;
    this.errorKeys = [];
    this.auth.requestEmailCode(this.challenge)
      .pipe(timeout(LoginComponent.SSO_TIMEOUT_MS), first())
      .subscribe({
        next: () => {
          this.busy = false;
          this.emailCodeSent = true;
          this.mfaMethod = 'email';
          this.code = '';
        },
        error: (err) => {
          this.busy = false;
          this.errorKeys = authErrorKeys(err);
        }
      });
  }

  /** Step two: the second factor. This is what produces a session. */
  submitMfa() {
    if (!this.code || this.busy) return;
    this.busy = true;
    this.errorKeys = [];
    this.auth.verifyMfa(this.challenge, this.code, this.rememberDevice).subscribe({
      next: (res) => this.completeLogin(res.token),
      error: (err) => {
        this.busy = false;
        this.code = '';
        this.errorKeys = authErrorKeys(err);
        this.restartIfChallengeIsDead(err);
      }
    });
  }

  submitRecovery() {
    if (!this.recoveryCode || this.busy) return;
    this.busy = true;
    this.errorKeys = [];
    this.auth.verifyRecovery(this.challenge, this.recoveryCode, this.rememberDevice).subscribe({
      next: (res) => this.completeLogin(res.token),
      error: (err) => {
        this.busy = false;
        this.recoveryCode = '';
        this.errorKeys = authErrorKeys(err);
        this.restartIfChallengeIsDead(err);
      }
    });
  }

  /**
   * A challenge lasts five minutes. Once it is gone, every code typed against
   * it is refused no matter how right it is -- so the screen goes back to the
   * password step instead of leaving someone retyping a correct code into a
   * challenge that cannot accept it. The message stays on screen through the
   * move, which is why this does not call backToPassword().
   */
  private restartIfChallengeIsDead(err: any) {
    const code = err && err.error && err.error.errorCode;
    if (code !== 'CHALLENGE_INVALID' && code !== 'CHALLENGE_EXPIRED') return;
    this.status = 'password';
    this.challenge = '';
    this.code = '';
    this.recoveryCode = '';
  }

  useRecovery() {
    this.status = 'recovery';
    this.errorKeys = [];
    this.recoveryCode = '';
  }

  backToPassword() {
    this.status = 'password';
    this.errorKeys = [];
    this.challenge = '';
    this.code = '';
    this.recoveryCode = '';
  }

  onEnrolled(token: string) {
    this.completeLogin(token);
  }

  /**
   * Stores the session and leaves the page.
   *
   * `window.location.href` rather than Router.navigate, matching the SSO branch
   * above: the full reload is what re-bootstraps app state with the new token.
   * `safeReturnUrl` is reused so a `returnUrl` on this route cannot send the user
   * off-origin after a genuine login.
   */
  private completeLogin(token: string) {
    this.busy = false;
    this.auth.storeSession(token);
    this.auth.setLoginStatus(true);
    this.auth.setUserEnabled(true);
    window.location.href = this.safeReturnUrl(this.retUrlParam);
  }

  signInWithSso() {
    this.redirectToSso();
  }

  // `returnUrl` arrives straight from the query string, and AuthGuard forwards it
  // into this route, so any deep link can carry it. Assigning it to
  // window.location.href unvalidated allowed an attacker to send a victim a link
  // on the genuine application domain that redirected to an off-site phishing
  // clone after a real login, and a `javascript:` value would execute in this
  // origin where it can read the access_token from localStorage.
  //
  // Only same-origin relative paths are accepted. window.location.href is kept
  // (rather than Router.navigateByUrl) so the full page reload after the SSO
  // token exchange is preserved, which is what re-bootstraps app state with the
  // new token.
  safeReturnUrl(value: any): string {
    if (typeof value !== 'string' || value.length === 0) {
      return '/';
    }
    // Must be a rooted relative path. This rejects absolute URLs
    // ("https://evil"), scheme URIs ("javascript:", "data:") and
    // protocol-relative URLs ("//evil", and "/\evil" which some browsers
    // normalise to the same thing).
    if (!value.startsWith('/') || value.startsWith('//') || value.startsWith('/\\')) {
      return '/';
    }
    return value;
  }

  // The SSO redirect link comes from the server, but the path appended to it is
  // caller-controlled. Encoding each segment keeps ordinary route segments
  // unchanged while preventing an injected "/", "?" or "#" from restructuring
  // the URL the user is sent to.
  safePathSegment(value: any): string {
    return encodeURIComponent(String(value ?? ''));
  }

  handleError(err) {
    console.error(err);
    this.status = 'error';
  }
}
